/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core';
import { claimCart, createNewCart } from '@msdyn365-commerce/global-state';

import { clearActiveCartMirrors, commitCartScope, forgetCart, getCartScopeKey, isCompleteCartScope, readCartScope } from './cartscope';

/** The cart the site is on, as the platform's own cookie names it. */
const readCartCookieId = (context: ICoreContext): string | undefined => {
    const cartCookie: string = context.request.cookies.getCartCookie();

    return cartCookie?.split(':')[1] || undefined;
};

/** Starts a cart the site has not used before and hands back its id. */
export const startNewCart = async (context: ICoreContext): Promise<string | undefined> => {
    await createNewCart(context.actionContext);

    const newCartId = readCartCookieId(context);
    if (!newCartId) {
        console.log('LTS Info', 'cartsession: a new cart was started but the site was left without its id');
        return undefined;
    }

    try {
        await claimCart(newCartId, context.actionContext);
    } catch (error) {
        console.log('LTS Info', 'cartsession: error claiming the new cart, but continuing', error);
    }

    return newCartId;
};

/**
 * Takes the site off the cart it is on, for good, and onto a new one, moving the suite/event pair with it.
 * Hands back the new cart, or undefined when the site could not be moved at all.
 */
export const retireActiveCart = async (context: ICoreContext): Promise<string | undefined> => {
    const retiredCartId = readCartCookieId(context);

    let newCartId: string | undefined;

    try {
        newCartId = await startNewCart(context);
    } catch (error) {
        console.log('LTS Info', 'cartsession: could not start a cart to replace the retired one', error);
    }

    if (!newCartId || newCartId === retiredCartId) {
        console.log('LTS Info', 'cartsession: the site could not be moved off the cart being retired', { retiredCartId });
        return undefined;
    }

    forgetCart(retiredCartId);
    clearActiveCartMirrors();

    const scope = readCartScope();
    if (isCompleteCartScope(scope)) {
        commitCartScope(getCartScopeKey(scope), newCartId);
    }

    return newCartId;
};
